import type { NpcVisual } from './appearance.js';

export interface NpcDef {
  id: string;
  name: string;
  title: string;
  visual: NpcVisual;
  greeting: string[];
  shopId?: string;
  /** Ambient villagers stroll within this radius of their post. */
  wanderRadius?: number;
}

export const NPCS: Readonly<Record<string, NpcDef>> = {
  elder_bai: {
    id: 'elder_bai',
    name: 'Bai Qingsong',
    title: 'Village Elder',
    visual: {
      gender: 'male',
      hairstyle: 'topknot',
      hairColor: '#d9d4c7',
      face: 'gentle',
      skinTone: '#e8c4a0',
      outfitStyle: 'scholar_gown',
      outfitColor: '#5e4a7a',
      beard: true,
      prop: 'staff',
    },
    greeting: [
      'Willow Creek has stood beside this stream for three hundred years. It will stand three hundred more, if its young people are brave.',
      'Patience is the first sword technique. The rest follow from it.',
    ],
  },
  guard_captain_wen: {
    id: 'guard_captain_wen',
    name: 'Wen Tie',
    title: 'Captain of the Watch',
    visual: {
      gender: 'male',
      hairstyle: 'short_crop',
      hairColor: '#1b1714',
      face: 'stern',
      skinTone: '#d6a77e',
      outfitStyle: 'wanderer_coat',
      outfitColor: '#7a2626',
      hat: 'guard_helmet',
      prop: 'spear',
    },
    greeting: [
      'Eyes open, disciple. The roads are not as quiet as they were.',
      'Bandits to the east, wolves to the north. And they call this the peaceful corner of the empire.',
    ],
  },
  herbalist_lin: {
    id: 'herbalist_lin',
    name: 'Lin Yao',
    title: 'Herbalist',
    visual: {
      gender: 'female',
      hairstyle: 'braided_crown',
      hairColor: '#3a2a20',
      face: 'gentle',
      skinTone: '#f3d9c0',
      outfitStyle: 'scholar_gown',
      outfitColor: '#2f4a33',
      prop: 'basket',
    },
    greeting: [
      'Pills for the wounded, brews for the weary. What do you need?',
      'I buy pelts and fangs, too — the caravans pay in good silver.',
    ],
    shopId: 'lin_apothecary',
  },
  smith_zhou: {
    id: 'smith_zhou',
    name: 'Zhou Gang',
    title: 'Blacksmith',
    visual: {
      gender: 'male',
      hairstyle: 'short_crop',
      hairColor: '#3a2a20',
      face: 'fierce',
      skinTone: '#b9825a',
      outfitStyle: 'wanderer_coat',
      outfitColor: '#1d1d22',
      prop: 'hammer',
    },
    greeting: [
      'Iron remembers every blow. So does a swordsman’s arm.',
      'The forge is cold until the ore caravan arrives. Bring me something worth tempering when it does.',
    ],
  },
  villager_mei: {
    id: 'villager_mei',
    name: 'Auntie Mei',
    title: 'Villager',
    visual: {
      gender: 'female',
      hairstyle: 'twin_buns',
      hairColor: '#5a3a26',
      face: 'calm',
      skinTone: '#e8c4a0',
      outfitStyle: 'disciple_robe',
      outfitColor: '#c9a35a',
      hat: 'straw',
    },
    greeting: ['Have you eaten yet? The dumpling stall by the well is the best in three counties.'],
    wanderRadius: 7,
  },
  villager_ah_fu: {
    id: 'villager_ah_fu',
    name: 'Ah Fu',
    title: 'Farmer',
    visual: {
      gender: 'male',
      hairstyle: 'long_tail',
      hairColor: '#1b1714',
      face: 'calm',
      skinTone: '#b9825a',
      outfitStyle: 'wanderer_coat',
      outfitColor: '#2c3e57',
      hat: 'straw',
    },
    greeting: ['The rice terraces south of the village need rain more than they need swords.'],
    wanderRadius: 8,
  },
};

export interface ShopDef {
  id: string;
  name: string;
  sells: { itemId: string; price: number }[];
  /** Item categories the shopkeeper buys back at the item's sell price. */
  buysCategories: string[];
}

export const SHOPS: Readonly<Record<string, ShopDef>> = {
  lin_apothecary: {
    id: 'lin_apothecary',
    name: 'Lin’s Apothecary',
    sells: [
      { itemId: 'minor_healing_pill', price: 8 },
      { itemId: 'minor_qi_pill', price: 10 },
    ],
    buysCategories: ['material', 'weapon', 'armor', 'consumable'],
  },
};

export function getNpc(id: string): NpcDef | undefined {
  return NPCS[id];
}

export function getShop(id: string): ShopDef | undefined {
  return SHOPS[id];
}
